import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { Tables, Json } from '@/integrations/supabase/types';
import { ServiceTabConfig, BusinessType } from '@/data/serviceTabPresets';
import { debug } from '@/lib/debug';

export type Organization = Tables<'organizations'> & {
  service_tabs?: ServiceTabConfig[];
  business_type?: string | null;
  intake_form_name?: string | null;
  intake_bg_color?: string | null;
  intake_text_color?: string | null;
  intake_accent_color?: string | null;
};

/**
 * Thrown by useCreateOrganization when the signed-in user's email
 * matches exactly one existing studio's intake history. The caller
 * (OnboardingWizard) is expected to catch this, surface a recovery
 * confirmation UI, and re-invoke with { allowDuplicate: true } if
 * the user explicitly chooses to proceed.
 */
export class OrgRecoveryCandidateError extends Error {
  candidate: { org_id: string; org_name: string | null; org_slug: string | null };
  constructor(candidate: { org_id: string; org_name: string | null; org_slug: string | null }) {
    super('RECOVERY_CANDIDATE_FOUND');
    this.name = 'OrgRecoveryCandidateError';
    this.candidate = candidate;
  }
}

// Helper to parse Supabase errors into user-friendly messages
// SIGNUP-HOTFIX — every branch below now carries the Postgres code and
// the raw message through in a `[code] raw` suffix.
//
// This function's job is a sentence a photographer can read. Its
// side-effect was that the sentence was ALL that survived: a 42501 RLS
// denial and a 23505 slug collision both reached the onboarding wizard
// as prose with no code attached, the wizard's own matcher failed to
// recognise its own output (see the toast in handleSaveOrg), and the
// studio — and Rennan reading the report — got "Failed to save
// organization. Please try again." for every distinct cause.
//
// A cause you cannot see is a cause you cannot fix. The suffix is ugly
// and it stays until signup has been quiet for a while.
const withCause = (friendly: string, code?: string, raw?: string): string => {
  const detail = [code, raw].filter(Boolean).join(' ');
  return detail ? `${friendly} [${detail}]` : friendly;
};

const parseOrgError = (error: unknown): string => {
  if (!error) return 'Unknown error occurred';
  
  const errorObj = error as { code?: string; message?: string; details?: string };
  const code = errorObj.code;
  const message = errorObj.message || '';
  
  // Duplicate key violations
  if (code === '23505' || message.includes('duplicate')) {
    if (message.includes('slug')) {
      return withCause('This URL slug is already in use. Please choose a different one.', code, message);
    }
    return withCause('A record with this value already exists.', code, message);
  }
  
  // RLS policy violations
  if (code === '42501' || message.includes('policy')) {
    return withCause('Permission denied. Please sign in again or contact support.', code, message);
  }
  
  // NOT NULL violations
  if (code === '23502' || message.includes('null value')) {
    const match = message.match(/column "([^"]+)"/);
    const column = match ? match[1] : 'required field';
    return withCause(`Missing required field: ${column}`, code, message);
  }
  
  // Check constraint violations
  if (code === '23514') {
    return withCause('Invalid data format. Please check your input.', code, message);
  }
  
  // Network errors
  if (message.includes('network') || message.includes('fetch')) {
    return 'Network error. Please check your connection and try again.';
  }
  
  // SIGNUP-HOTFIX — an unrecognised failure keeps its code too. This
  // is the branch a brand-new class of bug arrives through, so it is
  // the one that must say the most, not the least.
  return withCause(message || 'Failed to save. Please try again.', code, undefined);
};

export const useOrganization = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['organization', user?.id],
    queryFn: async () => {
      if (!user) return null;
      
      debug.log('Fetching organization for user', { userId: user.id });
      
      // Get profile to find org_id
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('id', user.id)
        .maybeSingle();
      
      if (profileError) {
        debug.error({ action: 'fetch_profile', userId: user.id }, profileError);
        return null;
      }
      
      // If no org_id, try to recover the binding
      if (!profile?.org_id) {
        debug.log('No org_id found, attempting recovery', { userId: user.id });
        
        // Call the recovery function
        const { data: recoveredOrgId, error: recoveryError } = await supabase
          .rpc('ensure_user_org_binding', { _user_id: user.id });
        
        if (recoveryError) {
          debug.error({ action: 'recover_org_binding', userId: user.id }, recoveryError);
          return null;
        }
        
        if (!recoveredOrgId) {
          debug.log('No organization found for user', { userId: user.id });
          return null;
        }
        
        // Fetch organization with recovered org_id
        const { data, error } = await supabase
          .from('organizations')
          .select('*')
          .eq('id', recoveredOrgId)
          .maybeSingle();
        
        if (error) {
          debug.error({ action: 'fetch_organization', orgId: recoveredOrgId }, error);
          throw error;
        }
        
        return data as Organization | null;
      }
      
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', profile.org_id)
        .maybeSingle();
      
      if (error) {
        debug.error({ action: 'fetch_organization', orgId: profile.org_id }, error);
        throw error;
      }
      
      debug.success('Organization fetched', { orgId: data?.id });
      return data as Organization | null;
    },
    enabled: !!user,
    // Stop refetching every time the tab gets focus. The org row
    // basically never changes during an active session, and the
    // transient `null` returned during a network blip on resume was
    // propagating into OnboardingWizard + ProtectedRoute and getting
    // studios stuck on the Nova step ("stuck on Nova onboarding when
    // inactive" reports from beta). 5-minute stale window matches
    // useStudioTimezone + useStudioBranding for consistency.
    refetchOnWindowFocus: false,
    staleTime: 5 * 60_000,
  });
};

/** UUIDs and slugs both land on this hook because Studio Sites
 *  generate intake form URLs with org UUIDs in the slug position
 *  (see ContactFormSection in PublicSite.tsx). Anything that matches
 *  this regex gets routed through the by_id path; everything else is
 *  treated as a slug. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const useOrganizationBySlug = (slug: string | undefined) => {
  return useQuery({
    queryKey: ['organization-by-slug', slug],
    queryFn: async () => {
      if (!slug) return null;

      debug.log('Fetching organization by slug', { slug });

      const isUuid = UUID_RE.test(slug);

      // Security Phase 1 locked `organizations` to org-members-only SELECT,
      // so anonymous reads (intake forms, /book, embedded forms) hit a
      // dead row. We resolve the org through the `public-org-lookup`
      // edge function which uses the service-role key and returns only
      // a tight allowlist of public-safe fields — never Stripe ids.
      //
      // Fallback chain (in order):
      //   1. Edge function — works as soon as it's deployed
      //   2. SECURITY DEFINER RPC — works once the SQL migration is run
      //   3. Direct table read — only works pre-Phase-1, kept as last ditch
      //
      // Each step also branches on isUuid so the UUID-embedded URLs
      // generated by Studio Sites resolve correctly. First win
      // short-circuits the chain.

      // 1) Edge function
      try {
        const { data: efData, error: efError } = await supabase.functions.invoke(
          'public-org-lookup',
          {
            body: isUuid
              ? { mode: 'by_id', org_id: slug }
              : { mode: 'by_slug', slug },
          },
        );
        if (!efError && efData && (efData as any).data) {
          return (efData as any).data as Organization;
        }
      } catch (_err) {
        /* edge fn not deployed yet — try next */
      }

      // 2) RPC — only the by_slug RPC exists at the moment; for UUID
      //    inputs there's no RPC equivalent yet, so we let this branch
      //    fall through to step 3 below.
      if (!isUuid) {
        try {
          const { data: rpcData, error: rpcError } = await (supabase as any)
            .rpc('get_public_org_by_slug', { _slug: slug })
            .maybeSingle();
          if (!rpcError && rpcData) {
            return rpcData as Organization;
          }
        } catch (_err) {
          /* RPC not yet created — fall through */
        }
      }

      // 3) Direct read (will fail under Phase 1 RLS, but kept so this
      //    behaves correctly if the lockdown is ever rolled back)
      let directQuery = supabase
        .from('organizations')
        .select(`
          id,
          name,
          slug,
          logo_url,
          brand_color,
          business_type,
          intake_bg_color,
          intake_text_color,
          intake_accent_color,
          created_at,
          updated_at
        `);
      directQuery = isUuid ? directQuery.eq('id', slug) : directQuery.eq('slug', slug);

      const { data, error } = await directQuery.maybeSingle();

      if (error) {
        debug.error({ action: 'fetch_org_by_slug', payload: { slug } }, error);
        throw error;
      }

      return data as Organization | null;
    },
    enabled: !!slug,
  });
};

export const useCreateOrganization = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (orgData: { 
      name: string; 
      slug: string; 
      brand_color?: string;
      business_type?: BusinessType | null;
      service_tabs?: ServiceTabConfig[];
      aesthetic_tags?: string[];
      /**
       * Set to true after the user has explicitly confirmed they want a new
       * studio even though a recovery candidate matched their email. Defaults
       * to false so the recovery guard is on by default.
       */
      allowDuplicate?: boolean;
    }) => {
      // NOTE: `service_tabs` is stored in the inquiry_categories table, not organizations,
      // so we drop it from the payload here. The onboarding wizard can save categories
      // separately if needed.
      const { service_tabs, aesthetic_tags, allowDuplicate, ...rest } = orgData;
      
      debug.log('Creating organization', { 
        userId: user?.id, 
        orgData: { name: orgData.name, slug: orgData.slug } 
      });
      
      // Validate required fields
      if (!orgData.name?.trim()) {
        throw new Error('Studio name is required');
      }
      
      if (!orgData.slug?.trim()) {
        throw new Error('URL slug is required');
      }
      
      // Validate slug format
      const slugRegex = /^[a-z0-9-]+$/;
      if (!slugRegex.test(orgData.slug)) {
        throw new Error('URL slug can only contain lowercase letters, numbers, and hyphens');
      }

      // Recovery guard — never silently create a duplicate org when the
      // signed-in user's email already matches an existing studio's
      // intake history. The caller must catch OrgRecoveryCandidateError
      // and surface a confirmation UI before retrying with allowDuplicate.
      if (user && !allowDuplicate) {
        // Tier A — if ensure_user_org_binding can already resolve an
        // existing org for this user (cached profile pointer, admin
        // membership, any membership, or single-email inquiry match),
        // skip the insert entirely and resolve with that org. This is
        // the silent self-heal path: the user clearly belongs to an
        // existing studio, no UI prompt needed.
        const { data: boundOrgId, error: bindErr } = await supabase
          .rpc('ensure_user_org_binding', { _user_id: user.id });
        if (bindErr) {
          debug.warn?.('ensure_user_org_binding failed — proceeding without silent rebind', bindErr);
        } else if (boundOrgId) {
          debug.success('Existing org binding resolved — skipping insert', { orgId: boundOrgId });
          const { data: existingOrg, error: fetchErr } = await supabase
            .from('organizations')
            .select('*')
            .eq('id', boundOrgId as string)
            .maybeSingle();
          if (!fetchErr && existingOrg) {
            return existingOrg as Organization;
          }
          debug.warn?.('Bound org id returned but fetch failed — falling through', fetchErr);
        }

        // Tier B — explicit recovery candidate (requires user confirmation).
        const { data: candidates, error: candErr } = await supabase
          .rpc('find_email_recovery_candidate', { _user_id: user.id });
        if (candErr) {
          debug.warn?.('find_email_recovery_candidate failed — proceeding without guard', candErr);
        } else if (Array.isArray(candidates) && candidates.length > 0) {
          const c = candidates[0] as { org_id: string; org_name: string | null; org_slug: string | null };
          debug.warn?.('Recovery candidate found — blocking duplicate org create', c);
          throw new OrgRecoveryCandidateError(c);
        }
      }

      // Provision the org through `ensure_user_org_binding` so the
      // SELECT-for-existing + INSERT-new + UPDATE-profile happen inside
      // one transaction under a row lock on `profiles`. This is what
      // prevents the duplicate-org bug — see migration
      // `20260526_org_binding_atomic.sql` for the full reasoning. Two
      // racing onboarding completions (web + iOS for the same user) now
      // serialize: the second call sees the freshly-bound profile and
      // returns the same org id, instead of inserting a second row.
      //
      // The RPC handles the prior defensive paths internally:
      //   - aesthetic_tags column defaulting (the migration's INSERT
      //     coalesces nulls)
      //   - profile.org_id update
      //   - admin user_roles row
      // So this caller no longer needs the column-missing retry, the
      // post-insert profile UPDATE, or the role write.
      if (!user) {
        // The RPC's self-check requires auth.uid(). Without a session
        // we have nothing to bind to — keep the explicit error so the
        // UI surfaces this rather than silently failing.
        throw new Error('You must be signed in to create a business.');
      }

      const { data: provisionedOrgId, error: rpcError } = await supabase
        .rpc('ensure_user_org_binding', {
          _user_id: user.id,
          _name: rest.name,
          _slug: rest.slug,
          _brand_color: rest.brand_color,
          // STRICT-1 — the RPC arg is `text DEFAULT NULL`; the generated
          // client type is `string | undefined`, and our own field is
          // nullable. Coalesce rather than send null through a slot
          // typed for undefined.
          _business_type: rest.business_type ?? undefined,
          _aesthetic_tags: aesthetic_tags ?? [],
        });

      if (rpcError || !provisionedOrgId) {
        const errorMsg = parseOrgError(rpcError);
        debug.error({
          action: 'create_organization_rpc',
          userId: user.id,
          payload: { name: orgData.name, slug: orgData.slug },
        }, rpcError);
        throw new Error(errorMsg);
      }

      // The RPC returns just the id — fetch the full row so the caller
      // (onboarding wizard, useOrganization invalidation, etc.) gets the
      // same Organization shape the old direct-insert path returned.
      const { data: org, error: fetchErr } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', provisionedOrgId as string)
        .single();

      if (fetchErr || !org) {
        debug.error({
          action: 'fetch_provisioned_org',
          userId: user.id,
          orgId: provisionedOrgId,
        }, fetchErr);
        throw new Error(parseOrgError(fetchErr));
      }

      debug.success('Organization created via RPC', { orgId: org.id });
      return org as Organization;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization'] });
    },
  });
};

export const useUpdateOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, service_tabs, aesthetic_tags, ...updates }: Partial<Organization> & { 
      id: string;
      service_tabs?: ServiceTabConfig[];
      aesthetic_tags?: string[];
    }) => {
      debug.log('Updating organization', { orgId: id, fields: Object.keys(updates) });
      
      // Build clean update payload - only include defined, non-null values
      // NOTE: service_tabs is not a column in organizations table anymore — stored in inquiry_categories
      const cleanUpdates: Record<string, unknown> = {};
      
      // Only include fields that exist in the organizations table
      if (updates.name !== undefined) cleanUpdates.name = updates.name;
      if (updates.slug !== undefined) cleanUpdates.slug = updates.slug;
      if (updates.brand_color !== undefined) cleanUpdates.brand_color = updates.brand_color;
      if (updates.logo_url !== undefined) cleanUpdates.logo_url = updates.logo_url;
      // BRAND-1 — the studio's custom brand mark that replaces the
      // Orbit wordmark across app + emails (paid-tier gated in
      // useStudioBranding + resolveStudioBranding). Nullable so
      // "remove logo" writes null.
      if ((updates as { brand_logo_url?: string | null }).brand_logo_url !== undefined) {
        cleanUpdates.brand_logo_url = (updates as { brand_logo_url?: string | null }).brand_logo_url;
      }
      // BRAND-2 — per-surface logo variants (dark / light). The dark
      // writer also mirrors into brand_logo_url upstream so legacy
      // single-logo consumers never go blank; here we just whitelist
      // both nullable columns for the update payload.
      if (updates.brand_logo_dark_url !== undefined) {
        cleanUpdates.brand_logo_dark_url = updates.brand_logo_dark_url;
      }
      if (updates.brand_logo_light_url !== undefined) {
        cleanUpdates.brand_logo_light_url = updates.brand_logo_light_url;
      }
      if (updates.business_type !== undefined) cleanUpdates.business_type = updates.business_type;
      if (updates.intake_form_name !== undefined) cleanUpdates.intake_form_name = updates.intake_form_name;
      if (updates.intake_bg_color !== undefined) cleanUpdates.intake_bg_color = updates.intake_bg_color;
      if (updates.intake_text_color !== undefined) cleanUpdates.intake_text_color = updates.intake_text_color;
      if (updates.intake_accent_color !== undefined) cleanUpdates.intake_accent_color = updates.intake_accent_color;
      // IANA timezone — drives every booking-link math op. Without
      // this whitelist entry the column was stuck at the 'UTC'
      // default for every studio, which made /book/:slug show slots
      // offset by hours from what the studio set in SchedulingSettings.
      if ((updates as any).timezone !== undefined) cleanUpdates.timezone = (updates as any).timezone;
      // studio_label — how the studio is referred to across the app
      // ("Studio" / business name / personal name). Missing from the
      // whitelist previously → onboarding step "How should we refer to
      // you?" produced an empty UPDATE which `.single()` rejected with
      // "Cannot coerce the result to a single JSON object."
      if ((updates as any).studio_label !== undefined) cleanUpdates.studio_label = (updates as any).studio_label;
      if (aesthetic_tags !== undefined) cleanUpdates.aesthetic_tags = aesthetic_tags;
      // service_tabs is intentionally ignored — use useSaveInquiryCategories instead
      
      // Validate slug if provided
      if (cleanUpdates.slug) {
        const slugRegex = /^[a-z0-9-]+$/;
        if (!slugRegex.test(cleanUpdates.slug as string)) {
          throw new Error('URL slug can only contain lowercase letters, numbers, and hyphens');
        }
      }
      
      // Defensive aesthetic_tags handling: in envs where the column
      // hasn't been migrated yet, drop it from the payload and retry
      // rather than failing the whole org update. See useCreateOrganization
      // above for the same pattern.
      let { data, error } = await supabase
        .from('organizations')
        .update(cleanUpdates)
        .eq('id', id)
        .select()
        .single();

      const isMissingAestheticTagsColumn =
        error &&
        ((error as { code?: string }).code === '42703' ||
          /column.*aesthetic_tags.*does not exist/i.test(error.message ?? '')) &&
        'aesthetic_tags' in cleanUpdates;

      if (isMissingAestheticTagsColumn) {
        debug.warn?.('aesthetic_tags column missing on update — retrying without it');
        const { aesthetic_tags: _drop, ...withoutAesthetic } = cleanUpdates as Record<string, unknown> & {
          aesthetic_tags?: unknown;
        };
        const retry = await supabase
          .from('organizations')
          .update(withoutAesthetic)
          .eq('id', id)
          .select()
          .single();
        data = retry.data;
        error = retry.error;
      }

      if (error) {
        const errorMsg = parseOrgError(error);
        debug.error({
          action: 'update_organization', 
          orgId: id,
          payload: cleanUpdates 
        }, error);
        throw new Error(errorMsg);
      }
      
      debug.success('Organization updated', { orgId: id });
      return data as Organization;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization'] });
    },
  });
};
